import type { Permission } from "../types/access";

/**
 * The §12 permission catalogue — every permission this system knows about.
 *
 * Here rather than in either application because both need the same list:
 * the API seeds `permissions` from it (#132) and checks against it, and the
 * web fixtures build roles from it. Two copies would drift, and a key that
 * exists on one side and not the other is a checkbox that grants nothing.
 *
 * `key`, `label` and `description` only — `module` is derived below from the
 * key's first segment rather than hand-repeated, because a catalogue entry
 * whose `module` disagrees with its own key is exactly the kind of typo that
 * silently drops a checkbox from the wrong group in the role builder.
 *
 * The keys must match spec §12 exactly: the role builder, the nav guard and
 * the seed all read this one list.
 */
const CATALOGUE: readonly (readonly [
  key: string,
  label: string,
  description: string | null,
])[] = [
  ["dashboard.overview.view", "View dashboard", null],
  [
    "dashboard.revenue.view",
    "View revenue",
    "Money on the dashboard: revenue and average order value. Adds to " +
      "viewing the dashboard and the Sales report; it does not grant them.",
  ],

  ["orders.list.view", "View order list", null],
  ["orders.list.export", "Export order list", null],
  ["orders.detail.view", "View order detail", null],
  [
    "orders.detail.viewRaw",
    "View raw order payload",
    "The unmodified payload received from the channel, for debugging a " +
      "sync problem.",
  ],
  ["orders.history.view", "View order change history", null],
  ["orders.shipments.view", "View order shipments", null],
  ["orders.detail.editTags", "Edit order tags", null],
  ["orders.detail.editNote", "Edit order note", null],
  ["orders.detail.cancel", "Cancel order", null],
  [
    "orders.escalations.view",
    "View delivery escalations",
    "See the parcels that failed delivery, went quiet or were lost, copy " +
      "their AWBs and export them. Shows no customer details.",
  ],
  [
    "orders.escalations.manage",
    "Manage delivery escalations",
    "Mark escalations as sent to the courier, stop tracking one with a " +
      "reason, and reopen one. ERP-only: nothing is sent to Shopify.",
  ],
  [
    "orders.escalations.settings",
    "Change escalation email settings",
    "Set the address the escalation emails go to and the times of the " +
      "twice-daily status email.",
  ],
  [
    "orders.returns.receive",
    "Receive returns",
    "Confirm that a returned (RTO) parcel has arrived, check each item, " +
      "and put the good ones back into stock. Writes stock adjustments and " +
      "an order tag to Shopify.",
  ],
  [
    "orders.stage.override",
    "Change order stage by hand",
    "Change an order's stage to any stage, forward or back, with a comment " +
      "that is added to the order's comments, or put it back to automatic, " +
      "one order or many. ERP-only: nothing is sent to Shopify.",
  ],

  ["catalog.product.view", "View products", null],
  ["catalog.product.edit", "Edit product", null],
  ["catalog.product.bulkEdit", "Bulk edit products", null],
  ["catalog.variant.editHsn", "Edit variant HSN code", null],
  ["catalog.hsn.view", "View HSN master", null],
  ["catalog.hsn.manage", "Manage HSN master", null],
  ["catalog.list.export", "Export product list", null],

  ["inventory.levels.view", "View stock levels", null],
  ["inventory.levels.export", "Export stock levels", null],
  ["inventory.lowstock.view", "View low stock", null],
  [
    "inventory.lowstock.edit",
    "Set low stock thresholds",
    "Set or clear the threshold a variant is judged against at one " +
      "warehouse. ERP-only: nothing is sent to Shopify.",
  ],
  ["inventory.adjustment.create", "Create stock adjustment", null],

  ["warehouse.list.view", "View warehouses", null],
  ["warehouse.list.create", "Create warehouse", null],
  ["warehouse.list.edit", "Edit warehouse", null],
  ["warehouse.list.delete", "Delete warehouse", null],
  ["warehouse.transfer.view", "View transfers", null],
  ["warehouse.transfer.create", "Create transfer", null],
  [
    "warehouse.transfer.dispatch",
    "Dispatch transfer",
    "Marks the outgoing side of a two-step transfer as sent.",
  ],
  [
    "warehouse.transfer.receive",
    "Receive transfer",
    "Marks the incoming side of a two-step transfer as received, closing " +
      "the in-transit balance.",
  ],
  ["warehouse.transfer.cancel", "Cancel transfer", null],

  ["contacts.contact.view", "View contacts", null],
  ["contacts.contact.create", "Create contact", null],
  ["contacts.contact.edit", "Edit contact", null],
  ["contacts.contact.delete", "Delete contact", null],
  ["contacts.contact.export", "Export contacts", null],
  [
    "contacts.type.manage",
    "Manage contact types",
    "The user-defined categories a contact can be tagged with, e.g. " +
      '"Supplier" or "Distributor".',
  ],

  [
    "customers.view",
    "View customers",
    "The Customers section of Contacts: name, city and state, and each " +
      "customer's order count, total spent and last order. Customers are " +
      "Shopify's and read-only here.",
  ],
  [
    "customers.view_contact",
    "View customer contact details",
    "Phone numbers, email addresses and full addresses. Without it the " +
      "server does not send them at all. Adds to customers.view; it does " +
      "not grant it.",
  ],
  [
    "customers.annotate",
    "Annotate customers",
    "Add or change the ERP-only note and tags on a customer. Never sent to " +
      "Shopify. Adds to customers.view; it does not grant it.",
  ],

  ["gst.invoice.view", "View GST invoices", null],
  ["gst.invoice.generate", "Generate GST invoice", null],
  ["gst.invoice.cancel", "Cancel GST invoice", null],
  ["gst.creditnote.create", "Create credit note", null],
  [
    "gst.export.run",
    "Run GST export",
    "Produces the HSN summary and return-ready export files.",
  ],
  [
    "gst.settings.manage",
    "Manage GST settings",
    "Company GSTIN, registered state and invoice series configuration.",
  ],

  ["tasks.task.view", "View tasks", null],
  ["tasks.task.create", "Create task", null],
  ["tasks.task.edit", "Edit task", null],
  ["tasks.task.assign", "Assign task", null],
  ["tasks.task.delete", "Delete task", null],
  ["tasks.comment.create", "Comment on task", null],
  [
    "tasks.project.manage",
    "Manage projects",
    "Create and edit the projects tasks are grouped under.",
  ],

  [
    "comments.create",
    "Post comments",
    "Post comments on orders and customers. Editing and deleting your own " +
      "comments does not need it. Only in this ERP, never sent to Shopify. " +
      "Reading a thread needs permission to view the record it is on; this " +
      "adds to that and does not grant it.",
  ],
  [
    "comments.moderate",
    "Moderate comments",
    "Delete anyone's comment on an order or a customer. Adds to being able " +
      "to view the record; it does not grant it.",
  ],

  ["reports.sales.view", "View sales report", null],
  ["reports.products.view", "View product performance report", null],
  ["reports.inventory.view", "View inventory report", null],
  ["reports.gst.view", "View GST report", null],
  ["reports.any.export", "Export any report", null],

  ["docs.page.view", "View doc pages", null],
  ["docs.page.create", "Create doc page", null],
  ["docs.page.edit", "Edit doc page", null],
  ["docs.page.delete", "Delete doc page", null],
  ["docs.folder.manage", "Manage doc folders", null],
  [
    "docs.permissions.manage",
    "Manage doc folder permissions",
    "Grants or revokes a user's or group's access to a folder, on top of " +
      "holding the docs.* permission itself (FR-DOC-7, FR-DOC-8).",
  ],

  [
    "settings.users.manage",
    "Manage users",
    "Invite, suspend and reactivate accounts.",
  ],
  ["settings.groups.manage", "Manage groups", null],
  [
    "settings.roles.manage",
    "Manage roles",
    "Create roles and edit which permissions they hold.",
  ],
  ["settings.company.manage", "Manage company settings", null],
  [
    "settings.views.manage",
    "Manage saved views",
    "Rename, re-default or delete a saved view. Anyone who can view a " +
      "module can already create one for it.",
  ],
  ["settings.sync.view", "View sync status", null],
  [
    "settings.sync.trigger",
    "Trigger sync",
    "Forces an immediate reconcile sweep instead of waiting for the " +
      "hourly run, or an immediate tracking check of every open shipment.",
  ],
  [
    "settings.tags.manage",
    "Manage tags",
    "The shared tag vocabulary offered when tagging an order or a contact.",
  ],
  ["settings.audit.view", "View audit log", null],
];

export const PERMISSION_CATALOGUE: Permission[] = CATALOGUE.map(
  ([key, label, description]) => {
    const [module] = key.split(".");
    if (!module) throw new Error(`permission key without a module: ${key}`);
    return { key, module, label, description };
  },
);
